import React, { useRef, useState, useEffect, useLayoutEffect, useMemo, createContext, useContext } from 'react';
import { isServer, debounce, is, isPrimitive } from '@global-ecom/nitro-uds/utils';
import deepmerge from 'deepmerge';
import { tw } from 'twind';

const UDS_BREAKPOINTS = [
    '_',
    'mobile',
    '2xs',
    'xs',
    'sm',
    'tablet',
    'md',
    'desktop',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
];

const useResizeObserver = (elementRef) => {
    const isMounted = useRef(false);
    const newRef = useRef(null);
    const ref = elementRef || newRef;
    const [state, setState] = useState({ width: 0, height: 0 });
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        /* istanbul ignore if  */
        if (!ref.current || isServer)
            return;
        if (isMounted.current) {
            setState({
                width: Math.round(ref.current.offsetWidth),
                height: Math.round(ref.current.offsetHeight),
            });
        }
        // @ts-ignore
        const ro = new window.ResizeObserver(debounce((entries) => {
            requestAnimationFrame(() => {
                /* istanbul ignore if  */
                if (!Array.isArray(entries) || !entries.length)
                    return;
                const { width, height } = entries[0].contentRect;
                /* istanbul ignore if  */
                if (isMounted.current)
                    setState({ width: Math.round(width), height: Math.round(height) });
            });
        }, 100));
        const element = ref.current;
        ro.observe(element);
        return () => ro.unobserve(element);
    }, [ref.current]);
    return { ref, ...state };
};

/* istanbul ignore  */
const useIsomorphicLayoutEffect = isServer
    ? /* istanbul ignore next */
        useEffect
    : useLayoutEffect;

const useIntersectionObserver = (props = {}) => {
    const { threshold = 0.5, rootMargin, enabled = true, once = false } = props;
    const [inView, setInview] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!enabled)
            return;
        let observer;
        if (ref.current && !isServer) {
            observer = new IntersectionObserver(([element]) => {
                setInview(element.isIntersecting);
                if (once && element.isIntersecting) {
                    observer.disconnect();
                }
            }, {
                threshold: threshold,
                rootMargin,
            });
            observer.observe(ref.current);
        }
        return () => {
            if (observer)
                observer.disconnect();
        };
    }, [enabled, ref]);
    return [ref, inView];
};

const useVisibilityChange = () => {
    const [isVisible, setIsVisible] = useState(true);
    const handleOnChange = () => {
        setIsVisible(document.visibilityState === 'visible');
    };
    useIsomorphicLayoutEffect(() => {
        document.addEventListener('visibilitychange', handleOnChange);
        return () => {
            document.removeEventListener('visibilitychange', handleOnChange);
        };
    }, []);
    return isVisible;
};

const useDelayUnmount = (isMounted, delayTime) => {
    const [shouldRender, setShouldRender] = useState(false);
    useEffect(() => {
        let timeoutId;
        if (isMounted && !shouldRender) {
            setShouldRender(true);
        }
        else if (!isMounted && shouldRender) {
            timeoutId = setTimeout(() => setShouldRender(false), delayTime);
        }
        return () => clearTimeout(timeoutId);
    }, [isMounted, delayTime, shouldRender]);
    return shouldRender;
};

const useMediaQuery = (query) => {
    const mediaMatch = isServer
        ? /* istanbul ignore next */
            null
        : window.matchMedia(query);
    const [matches, setMatches] = useState(mediaMatch?.matches || false);
    useEffect(() => {
        /* istanbul ignore if */
        if (isServer)
            return;
        const handler = e => setMatches(e.matches);
        mediaMatch.addEventListener('change', handler);
        return () => mediaMatch.removeEventListener('change', handler);
    });
    return matches;
};

/**
 * Hook to convert responsive prop to a responsive tailwind utility string
 * @example
 *
 * ```jsx
 * const padding:ResponsiveProp<string> = {_: 'md', md: 'lg'};
 *
 * const paddingClassName = useResponsiveProp(padding, (value) => `p-${value}`);
 *
 * const className = tw(paddingClassName);
 * ```
 *
 * @param prop - responsive prop
 * @param callback - callback to convert the prop value to a tailwind utility string
 * @returns string
 */
function useResponsiveProp(prop, callback) {
    return useMemo(() => {
        const propObject = is.primitive(prop) ? { _: prop } : prop;
        return Object.keys(propObject).reduce((acc, key) => {
            const result = callback(propObject[key]);
            const prefix = key === '_' ? '' : `${key}:`;
            const out = Array.isArray(result)
                ? `${prefix}(${result.join(' ')})`
                : `${prefix}(${result})`;
            return `${acc} ${out.trim()}`;
        }, ``);
    }, [prop, callback]);
}

const initialDictionary = {
    next: 'Next',
    previous: 'Previous',
    scrollNext: 'Scroll Next',
    scrollPrevious: 'Scroll Previous',
    pageXofY: 'Page {page} of {total}',
    loading: 'Loading',
    optional: 'Optional',
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    day: 'Day',
    month: 'Month',
    year: 'Year',
    close: 'Close',
    clear: 'Clear',
    search: 'Search',
    current: 'Current',
    complete: 'Complete',
    stepCount: 'Step {step} of {total}',
    showPassword: 'Show Password',
    hidePassword: 'Hide Password',
    charactersRemaining: '{num} characters remaining',
    genericFieldMinLength: 'This field must be at least {length} characters in length.',
    genericFieldMaxLength: 'This field cannot exceed {length} characters in length.',
    srNewSearchResults: 'New search results for query {query}. {num} items. Use your directional controls to navigate through the results.',
    srNoSearchResults: 'No search results found for your query {query}. Press the escape key to clear the search field.',
};
const replaceVariable = (translation, key, value) => {
    return translation.replace(new RegExp(`{${key}}`, 'g'), value);
};
const getDictionaryTranslation = (dictionary) => {
    const translate = (key, variables) => {
        let translation = dictionary[key] ?? '';
        if (variables) {
            Object.keys(variables).forEach(vKey => {
                if (translation.length) {
                    translation = replaceVariable(translation, vKey, variables[vKey]);
                }
            });
        }
        return translation;
    };
    return translate;
};
const initialContext = {
    iconDirectory: '',
    dictionary: initialDictionary,
    devicePreferences: {
        prefersReducedMotion: false,
        prefersReducedData: false,
    },
    screens: {},
};
/**
 * Typically, just the UdsProvider is needed.
 * Use this to provide overrides.
 */
const UdsContext = createContext(initialContext);
/**
 * This Provider is required by all UDS elements.
 * Be sure to add this to the root of your application.
 */
const UdsProvider = ({ value = {}, children, }) => {
    const mediaQueries = useMemo(() => {
        return UDS_BREAKPOINTS.reduce((result, breakpoint) => {
            const twScreen = tw.theme(`screens`)[breakpoint];
            const mql = typeof twScreen === 'string'
                ? `screen and (min-width: ${twScreen})`
                : twScreen.raw;
            result[breakpoint] = mql;
            return result;
        }, {});
    }, [tw.theme(`screens`)]);
    const prefersReducedMotion = useMediaQuery('screen and (prefers-reduced-motion)');
    const prefersReducedData = useMediaQuery('screen and (prefers-reduced-data)');
    const getMqlMatches = () => {
        return UDS_BREAKPOINTS.reduce((matches, breakpoint) => {
            const mql = mediaQueries[breakpoint];
            if (breakpoint === '_')
                return matches;
            matches[breakpoint] = isServer ? false : window.matchMedia(mql).matches;
            return matches;
        }, { _: true });
    };
    const matches = getMqlMatches();
    const [mediaQueriesMatch, setMediaQueriesMatch] = useState(matches);
    const onResize = debounce(() => {
        const matches = getMqlMatches();
        setMediaQueriesMatch(matches);
    }, 100);
    useIsomorphicLayoutEffect(() => {
        /* istanbul ignore next */
        if (isServer)
            return;
        window.addEventListener('resize', onResize);
        /* istanbul ignore next */
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [mediaQueries]);
    const state = useMemo(() => {
        return deepmerge.all([
            initialContext,
            {
                screens: {
                    mql: mediaQueries || {},
                    mqlMatch: mediaQueriesMatch || {},
                    breakpoints: [...UDS_BREAKPOINTS],
                },
                devicePreferences: {
                    prefersReducedMotion,
                    prefersReducedData,
                },
            },
            value,
        ]);
    }, [
        value,
        prefersReducedMotion,
        prefersReducedData,
        mediaQueries,
        mediaQueriesMatch,
    ]);
    return React.createElement(UdsContext.Provider, { value: state }, children);
};
const useUdsContext = () => {
    return useContext(UdsContext);
};
const useUdsTranslate = () => {
    const { dictionary } = useContext(UdsContext);
    const t = getDictionaryTranslation(dictionary);
    return t;
};

function useResponsiveValue(value) {
    const propObject = isPrimitive(value) ? { _: value } : value;
    const { screens } = useUdsContext();
    let currentBreakpoint = '_';
    screens.breakpoints.forEach(breakpoint => {
        if (propObject[breakpoint] && screens.mqlMatch[breakpoint]) {
            currentBreakpoint = breakpoint;
        }
    });
    return propObject[currentBreakpoint];
}

function useResponsiveMap(prop, map) {
    return useMemo(() => {
        const propObject = is.primitive(prop) ? { _: prop } : prop;
        return Object.keys(propObject).reduce((acc, key) => {
            acc[key] = map[propObject[key]];
            return acc;
        }, {});
    }, [prop, map]);
}

function useSharedRef(a, b) {
    const ref = useMemo(() => {
        if (a === null && b === null) {
            return null;
        }
        return (value) => {
            if (typeof a === 'function') {
                a(value);
            }
            else if (a) {
                a.current = value;
            }
            if (typeof b === 'function') {
                b(value);
            }
            else if (b) {
                b.current = value;
            }
        };
    }, [a, b]);
    return ref;
}

const useHorizontalScroll = ({ infinite = true, items, } = {}) => {
    const { ref, width } = useResizeObserver();
    const [state, setState] = useState({
        hasPrev: !!infinite,
        hasNext: !!infinite,
        isOverflowing: false,
    });
    useEffect(() => {
        const scroller = ref.current;
        const update = () => setState({
            hasPrev: infinite || scroller.scrollLeft != 0,
            hasNext: infinite ||
                scroller.scrollLeft + scroller.offsetWidth < scroller.scrollWidth,
            isOverflowing: scroller.scrollWidth > scroller.clientWidth,
        });
        if (scroller) {
            debounce(update, 200)();
            scroller.addEventListener('scroll', debounce(update, 200));
        }
    }, [ref, width, items]);
    const scrollToNext = () => {
        const scroller = ref.current;
        const scrollByAmount = scroller.offsetWidth;
        if (scroller.scrollLeft + scrollByAmount + 1 < scroller.scrollWidth) {
            scroller.scrollBy({
                left: scroller.offsetWidth,
                top: 0,
                behavior: 'smooth',
            });
        }
        else if (infinite)
            scroller.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    };
    const scrollToPrev = () => {
        const scroller = ref.current;
        const scrollByAmount = scroller.offsetWidth;
        if (scroller.scrollLeft != 0) {
            scroller.scrollBy({
                left: -scrollByAmount,
                top: 0,
                behavior: 'smooth',
            });
        }
        else if (infinite)
            scroller.scrollTo({
                left: scroller.scrollWidth,
                top: 0,
                behavior: 'smooth',
            });
    };
    const scrollToIndex = (index) => {
        const scroller = ref.current;
        const scrollByAmount = scroller.offsetWidth;
        if (scroller.scrollLeft + scrollByAmount + 1 < scroller.scrollWidth) {
            scroller.scrollBy({
                left: scroller.offsetWidth * index,
                top: 0,
                behavior: 'auto',
            });
        }
        else if (infinite)
            scroller.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    };
    return { ref, scrollToNext, scrollToPrev, scrollToIndex, ...state };
};

function useNavigate({ href, target, popup = false, popupWidth = 750, popupHeight = 780, focusable = true, onClick, ...rest }) {
    const { utils } = useUdsContext();
    const newTabProps = target === '_blank' ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    const isAbsoluteUrl = href?.includes('://') ||
        href?.startsWith('mailto') ||
        href?.startsWith('tel');
    const localizedHref = utils?.localizeUrlPath && !isAbsoluteUrl
        ? utils.localizeUrlPath(href)
        : href;
    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
        if (popup) {
            e.preventDefault();
            return window.open(localizedHref, '', `
          scrollbars=yes,
          menubar=no,
          status=no,
          location=no,
          toolbar=no,
          width=${popupWidth},
          height=${popupHeight}},
          top=${(window.innerHeight - popupWidth) / 2},
          left=${(window.innerWidth - popupHeight) / 2}
          `
                .replace(/\\n/g, '')
                .trim());
        }
        const isModifiedEvent = e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;
        if (utils?.navigate && // custom navigate function is available
            !e.defaultPrevented && // onClick prevented default
            e.button === 0 && // ignore everything but left clicks
            (!target || target === '_self') && // let browser handle "target=_blank" etc.
            !isModifiedEvent && // ignore clicks with modifier keys
            !isAbsoluteUrl // ignore absolute urls
        ) {
            e.preventDefault();
            utils.navigate(localizedHref);
        }
    };
    return {
        ...rest,
        ...newTabProps,
        href: localizedHref,
        tabIndex: focusable ? undefined : -1,
        'aria-hidden': focusable ? undefined : true,
        onClick: handleClick,
    };
}

export { UDS_BREAKPOINTS, UdsContext, UdsProvider, initialDictionary, useDelayUnmount, useHorizontalScroll, useIntersectionObserver, useIsomorphicLayoutEffect, useMediaQuery, useNavigate, useResizeObserver, useResponsiveMap, useResponsiveProp, useResponsiveValue, useSharedRef, useUdsContext, useUdsTranslate, useVisibilityChange };
//# sourceMappingURL=index.js.map
